import {
	isValid,
	isAfter,
	isToday,
	isTomorrow,
	isYesterday,
	isThisWeek,
	isBefore,
	isSameDay,
	differenceInCalendarWeeks,
	differenceInCalendarYears,
	differenceInCalendarQuarters,
	startOfTomorrow,
	startOfToday,
	startOfYesterday,
	parseISO,
} from 'date-fns';
import type {
	JiraFormattingMultipleValueOperator,
	JiraFormattingNoValueOperator,
	JiraFormattingSingleValueOperator,
} from '@atlassian/jira-relay/src/__generated__/useSaveRuleOldMutation.graphql';
import {
	isDateRuleOnlyOperator,
	isMultipleValueOperator,
	isNoValueOperator,
	isNumber,
	isSingleValueOperator,
	isString,
	type DateRuleOnlyOperator,
	type DateRuleValue,
	type DateValue,
	type FieldValue,
	type FieldValuePrimitive,
	type FormattingRuleOperators,
	type RuleValue,
	type RuleValuePrimitive,
} from './types.tsx';

const evaluateRuleNoValue = (operator: JiraFormattingNoValueOperator, fieldValue: FieldValue) => {
	if (Array.isArray(fieldValue)) {
		switch (operator) {
			case 'IS_EMPTY':
				return fieldValue.length === 0;
			case 'IS_NOT_EMPTY':
				return fieldValue.length > 0;
			default:
				return false;
		}
	}

	switch (operator) {
		case 'IS_EMPTY':
			return fieldValue === 0 ? false : !fieldValue;
		case 'IS_NOT_EMPTY':
			return fieldValue === 0 ? true : Boolean(fieldValue);
		default:
			return false;
	}
};

const ruleSingleValueStringOperations: Pick<
	Record<
		JiraFormattingSingleValueOperator,
		(fieldValue: string | string[], ruleValue: RuleValuePrimitive) => boolean
	>,
	'CONTAINS'
> = {
	CONTAINS: (fieldValue, ruleValue) => {
		if (Array.isArray(fieldValue)) {
			return fieldValue.some((value) => {
				if (value == null) {
					return false;
				}
				if (isNumber(value)) {
					return value === Number(ruleValue);
				}
				if (isString(value)) {
					return value.includes(ruleValue);
				}
				return false;
			});
		}
		return fieldValue.includes(ruleValue);
	},
};
const ruleSingleValueOperations: Pick<
	Record<
		JiraFormattingSingleValueOperator,
		(fieldValue: FieldValue, ruleValue: RuleValuePrimitive) => boolean
	>,
	'EQUALS'
> = {
	EQUALS: (fieldValue, ruleValue) => {
		return Boolean(fieldValue && String(fieldValue) === ruleValue);
	},
};
const ruleSingleValueNumberOperations: Pick<
	Record<
		JiraFormattingSingleValueOperator,
		(fieldValue: number, ruleValue: RuleValuePrimitive) => boolean
	>,
	'GREATER_THAN'
> = {
	GREATER_THAN: (fieldValue, ruleValue) => {
		return fieldValue > Number(ruleValue);
	},
};

const dateRuleValueEvaluators: Pick<
	Record<
		DateRuleOnlyOperator,
		(fieldValue: DateValue, ruleValue: DateRuleValue | string) => boolean
	>,
	DateRuleOnlyOperator
> = {
	IS: (fieldValue, ruleValue) => {
		switch (ruleValue) {
			case 'TODAY':
				return isToday(fieldValue);
			case 'TOMORROW':
				return isTomorrow(fieldValue);
			case 'YESTERDAY':
				return isYesterday(fieldValue);
			case 'THIS_WEEK':
				return isThisWeek(fieldValue);
			case 'LAST_WEEK':
				return differenceInCalendarWeeks(fieldValue, new Date()) === -1;
			case 'NEXT_WEEK':
				return differenceInCalendarWeeks(fieldValue, new Date()) === 1;
			case 'THIS_QUARTER':
				return differenceInCalendarQuarters(fieldValue, new Date()) === 0;
			case 'LAST_QUARTER':
				return differenceInCalendarQuarters(fieldValue, new Date()) === -1;
			case 'NEXT_QUARTER':
				return differenceInCalendarQuarters(fieldValue, new Date()) === 1;
			case 'THIS_YEAR':
				return differenceInCalendarYears(fieldValue, new Date()) === 0;
			case 'LAST_YEAR':
				return differenceInCalendarYears(fieldValue, new Date()) === -1;
			case 'NEXT_YEAR':
				return differenceInCalendarYears(fieldValue, new Date()) === 1;
			default: {
				const dateValue = parseISO(ruleValue);
				if (isValid(dateValue)) {
					return isSameDay(dateValue, fieldValue);
				}
				return false;
			}
		}
	},
	IS_AFTER: (fieldValue, ruleValue) => {
		switch (ruleValue) {
			case 'TODAY':
				return isAfter(fieldValue, startOfToday());
			case 'TOMORROW':
				return isAfter(fieldValue, startOfTomorrow());
			case 'YESTERDAY':
				return isAfter(fieldValue, startOfYesterday());
			case 'THIS_WEEK':
				return differenceInCalendarWeeks(fieldValue, new Date()) > 0;
			case 'LAST_WEEK':
				return differenceInCalendarWeeks(fieldValue, new Date()) >= 0;
			case 'NEXT_WEEK':
				return differenceInCalendarWeeks(fieldValue, new Date()) > 1;
			case 'THIS_QUARTER':
				return differenceInCalendarQuarters(fieldValue, new Date()) > 0;
			case 'LAST_QUARTER':
				return differenceInCalendarQuarters(fieldValue, new Date()) >= 0;
			case 'NEXT_QUARTER':
				return differenceInCalendarQuarters(fieldValue, new Date()) > 1;
			case 'THIS_YEAR':
				return differenceInCalendarYears(fieldValue, new Date()) > 0;
			case 'LAST_YEAR':
				return differenceInCalendarYears(fieldValue, new Date()) >= 0;
			case 'NEXT_YEAR':
				return differenceInCalendarYears(fieldValue, new Date()) > 1;
			default: {
				const dateValue = parseISO(ruleValue);
				if (isValid(dateValue)) {
					return isAfter(fieldValue, dateValue);
				}
				return false;
			}
		}
	},
	IS_BEFORE: (fieldValue, ruleValue) => {
		switch (ruleValue) {
			case 'TODAY':
				return isBefore(fieldValue, startOfToday());
			case 'TOMORROW':
				return isBefore(fieldValue, startOfTomorrow());
			case 'YESTERDAY':
				return isBefore(fieldValue, startOfYesterday());
			case 'THIS_WEEK':
				return differenceInCalendarWeeks(fieldValue, new Date()) < 0;
			case 'LAST_WEEK':
				return differenceInCalendarWeeks(fieldValue, new Date()) < -1;
			case 'NEXT_WEEK':
				return differenceInCalendarWeeks(fieldValue, new Date()) < 1;
			case 'THIS_QUARTER':
				return differenceInCalendarQuarters(fieldValue, new Date()) < 0;
			case 'LAST_QUARTER':
				return differenceInCalendarQuarters(fieldValue, new Date()) < -1;
			case 'NEXT_QUARTER':
				return differenceInCalendarQuarters(fieldValue, new Date()) < 1;
			case 'THIS_YEAR':
				return differenceInCalendarYears(fieldValue, new Date()) < 0;
			case 'LAST_YEAR':
				return differenceInCalendarYears(fieldValue, new Date()) < -1;
			case 'NEXT_YEAR':
				return differenceInCalendarYears(fieldValue, new Date()) < 1;
			default: {
				const dateValue = parseISO(ruleValue);
				if (isValid(dateValue)) {
					return isBefore(fieldValue, dateValue);
				}
				return false;
			}
		}
	},
	IS_NOT: (field, value) => !dateRuleValueEvaluators.IS?.(field, value),
	IS_ON_OR_AFTER: (field, value) =>
		Boolean(
			dateRuleValueEvaluators.IS?.(field, value) ||
				dateRuleValueEvaluators.IS_AFTER?.(field, value),
		),
	IS_ON_OR_BEFORE: (field, value) =>
		Boolean(
			dateRuleValueEvaluators.IS?.(field, value) ||
				dateRuleValueEvaluators.IS_BEFORE?.(field, value),
		),
};

const evaluateRuleSingleValue = (
	operator: JiraFormattingSingleValueOperator,
	fieldValue: FieldValue,
	ruleValue: RuleValuePrimitive,
): boolean => {
	if (isDateRuleOnlyOperator(operator) && fieldValue instanceof Date) {
		return dateRuleValueEvaluators[operator](fieldValue, ruleValue) ?? false;
	}

	// Operators that apply to all field value types
	switch (operator) {
		case 'EQUALS':
			return ruleSingleValueOperations.EQUALS(fieldValue, ruleValue);
		case 'DOES_NOT_EQUAL':
			return !ruleSingleValueOperations.EQUALS(fieldValue, ruleValue);
		case 'IS':
			return fieldValue === ruleValue;
		case 'IS_NOT':
			return fieldValue !== ruleValue;
		default:
			break;
	}

	if (fieldValue == null) {
		// All single value rule requires the field value to exist
		return false;
	}

	if (isString(fieldValue) || Array.isArray(fieldValue)) {
		// Operators that does not apply to number or nullish fields
		switch (operator) {
			case 'CONTAINS':
				return ruleSingleValueStringOperations.CONTAINS(fieldValue, ruleValue);
			case 'DOES_NOT_CONTAIN':
				return !ruleSingleValueStringOperations.CONTAINS(fieldValue, ruleValue);
			default:
				return false;
		}
	}

	if (isNumber(fieldValue)) {
		switch (operator) {
			// Only applies to number
			case 'GREATER_THAN':
				return ruleSingleValueNumberOperations.GREATER_THAN(fieldValue, ruleValue) ?? false;
			case 'LESS_THAN':
				return (
					!(ruleSingleValueNumberOperations.GREATER_THAN(fieldValue, ruleValue) ?? true) &&
					!(ruleSingleValueOperations.EQUALS(fieldValue, ruleValue) ?? true)
				);
			case 'GREATER_THAN_OR_EQUALS':
				return (
					(ruleSingleValueNumberOperations.GREATER_THAN(fieldValue, ruleValue) ?? false) ||
					(ruleSingleValueOperations.EQUALS(fieldValue, ruleValue) ?? false)
				);
			case 'LESS_THAN_OR_EQUALS':
				return !(ruleSingleValueNumberOperations.GREATER_THAN(fieldValue, ruleValue) ?? true);
			default:
				break;
		}
	}

	return false;
};

const multipleValueOperatorSingleFieldValueEvaluators: Record<
	JiraFormattingMultipleValueOperator,
	(fieldValue: FieldValuePrimitive, ruleValue: RuleValuePrimitive[]) => boolean
> = {
	HAS_ANY_OF: (fieldValue, ruleValue) => {
		if (fieldValue == null) {
			return false;
		}

		return ruleValue.includes(String(fieldValue));
	},
	// No valid rule single field value contains a multiple rule value
	CONTAINS: () => false,
	DOES_NOT_CONTAIN: () => false,
	// eslint-disable-next-line @atlassian/relay/no-future-added-value
	'%future added value': () => false,
};

const multipleValueOperatorEvaluators: Record<
	JiraFormattingMultipleValueOperator,
	(fieldValue: FieldValuePrimitive[], ruleValue: RuleValuePrimitive[]) => boolean
> = {
	CONTAINS: (fieldValue, ruleValue) => {
		return (
			fieldValue.filter((fval) => {
				return ruleValue.includes(String(fval));
			}).length === ruleValue.length
		);
	},
	DOES_NOT_CONTAIN: (...args) => !multipleValueOperatorEvaluators.CONTAINS(...args),
	HAS_ANY_OF: (fieldValue, ruleValue) => {
		return fieldValue.some((fval) => {
			return multipleValueOperatorSingleFieldValueEvaluators.HAS_ANY_OF(fval, ruleValue);
		});
	},
	// eslint-disable-next-line @atlassian/relay/no-future-added-value
	'%future added value': () => false,
};

const evaluateRuleMultipleValue = (
	operator: JiraFormattingMultipleValueOperator,
	fieldValue: FieldValue,
	ruleValue: RuleValuePrimitive[],
) => {
	if (Array.isArray(fieldValue)) {
		return multipleValueOperatorEvaluators[operator](fieldValue, ruleValue);
	}
	return multipleValueOperatorSingleFieldValueEvaluators[operator](fieldValue, ruleValue);
};

export const evaluateRule = (
	operator: FormattingRuleOperators,
	fieldValue: FieldValue,
	ruleValue?: RuleValue,
) => {
	if (ruleValue === undefined) {
		if (isNoValueOperator(operator)) {
			return evaluateRuleNoValue(operator, fieldValue);
		}
		return false;
	}
	if (Array.isArray(ruleValue) && isMultipleValueOperator(operator)) {
		return evaluateRuleMultipleValue(operator, fieldValue, ruleValue);
	}

	if (isSingleValueOperator(operator) && typeof ruleValue === 'string') {
		return evaluateRuleSingleValue(operator, fieldValue, ruleValue);
	}

	// We will need to handle this in the hook, and sent analytics event when missing.
	// throw new Error('Unhandled format rule'); https://jbusiness.atlassian.net/browse/JSC-2764 Uncomment when error is properly handled
};
